import React from 'react'
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { FormControl, FormHelperText, Grid, FormControlLabel, Checkbox, TextField, MenuItem, Select } from '@mui/material';
import { Form, Formik } from 'formik';
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { FMTextField } from '../Elements/input';
import useAuth from "../../context/AuthProvider";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { apiClient } from '../../helper/api';
import Swal from "sweetalert2";
import * as Yup from 'yup';

interface LicenseesContactInformation {
  partnerLincenseeInformationId: number;
  customerName: string;
  partnersId: number;
  postalAbbvs: string;
  licenseeName: string;
  pageSize : number;
  pageNumber : number;
  cancellationDate: string;
  entity: boolean;
  fein: string;
  licenseNumber: string;
  licenseState: string;
  licensee: string;
  mainLicensee: boolean;
  npn: string;
  title: string;
}

interface Iprops {
  licenseesContactInformation: LicenseesContactInformation,
  handleClose: () => void,
  Open: boolean,
  handleSubmit: () => void
  initValues: LicenseesContactInformation
  fetchLicenseMatrixData: (pageNumber: number, pageSize: number) => Promise<any>;
  pageNumber: number;
  pageSize: number;
  States: { stateId: number, stateName: string, postalAbbv: string }[];
  Partners: { partnersId: number, customerName: string}[];
}

export const AddEditLicenseContact: React.FC<Iprops> = ({
  handleClose, Open, licenseesContactInformation, handleSubmit, initValues, fetchLicenseMatrixData, pageNumber, pageSize, States, Partners
}) => {
  const initialValues: LicenseesContactInformation = licenseesContactInformation ?  
        licenseesContactInformation : initValues;
  const { user } = useAuth();
  const token = user?.token;
  const [SelectedPartner, setSelectedPartner] = React.useState<number>(initialValues.partnersId);

  const requiredString = (message: string) => Yup.string().required(message);
    const validationSchema = Yup.object({
    npn: requiredString('NPN is required'),
    licenseState: requiredString('State is required'),
    partnersId: requiredString('Partner is required'),
    licensee: requiredString('Licensee is required'),
    licenseNumber: requiredString('License Number is required'),
    mainLicensee: requiredString('Main Licensee is required'),
    entity: requiredString('Entity is required'),
  });

  async function saveLicensee(data: any) {
    const url = '/LicenseMatrix/AddLicensee';
    const requestData = {
      Licensee: data.licensee,
      LicenseState: data.licenseState,
      LicenseNumber: data.licenseNumber,
      Title: data.title,
      PartnersId: SelectedPartner,
      MainLicensee: data.mainLicensee,
      Entity: data.entity,
      Npn: data.npn,
      Fein: data.fein,
      CancellationDate: data.cancellationDate
    };

    try {
      Swal.fire({
        title: 'Saving...',
        text: 'Please wait while we save your data.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      }).then(() => {
        fetchLicenseMatrixData(pageNumber,pageSize);
      });
      const response = await apiClient(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      if (response.resp === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Data added successfully.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 1) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: response.message || 'Failed to add data.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 2) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Unexpected response from the server.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'An unexpected error occurred. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  const updateLicenseesContactInformation = async (updatedData: any) => {
    try {
      Swal.fire({
        title: 'Updating...',
        text: 'Please wait while we update your data.',
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        }
      });

      const response = await apiClient(`/LicenseMatrix/UpdateLicensee`, {
        method: "POST",
        body: JSON.stringify(updatedData),
        headers: new Headers({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        })
      });

      if (response.resp === 0) {
        Swal.fire({
          icon: 'success',
          title: 'Success!',
          text: 'Data updated successfully.',
          confirmButtonText: 'OK'
        }).then(() => {
          fetchLicenseMatrixData(pageNumber,pageSize);
        });
      } else if (response.resp === 1) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: response.message || 'Failed to add data.',
          confirmButtonText: 'OK'
        });
      } else if (response.resp === 2) {
        Swal.fire({
          icon: 'error',
          title: 'Error!',
          text: 'Unexpected response from the server.',
          confirmButtonText: 'OK'
        });
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: 'An unexpected error occurred. Please try again.',
        confirmButtonText: 'OK'
      });
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={Open} onClose={handleClose}>
        <DialogTitle className='card-title'>Licensee Contact</DialogTitle>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={(data, { setSubmitting }) => {
            if (data.partnerLincenseeInformationId === 0) {
              saveLicensee(data);
            } else {
              updateLicenseesContactInformation([{...data, partnersId: (SelectedPartner == 0 ? data.partnersId : SelectedPartner)}]);
              setSubmitting(false);
            }
            setSelectedPartner(0);
            handleSubmit();
          }}
        >
          {({ values, isSubmitting, errors, setFieldValue }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <FormControl error={!!errors.licenseState} sx={{ width: '100%' }}>
                      <Select
                        value={values.licenseState}
                        id="test-select-label"
                        label={null}
                        onChange={(e) => {
                          setFieldValue("licenseState", e.target.value);
                        }}
                        name="licenseState"
                        className="border-round custom-menu-item"
                        IconComponent={ExpandMoreIcon}
                        displayEmpty
                        renderValue={(selected) => {
                          const selectedState = States.find((state) => state.postalAbbv === selected);
                          return selectedState ? selectedState.stateName : "Select State";
                        }}
                      >
                        <MenuItem key={0} value={0}>
                          Select State{" "}
                        </MenuItem>
                        {States.map((state, key) => (
                          <MenuItem key={key} value={state.postalAbbv}>
                            {state.stateName}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>{errors.licenseState}</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl error={!!errors.partnersId} sx={{ width: '100%' }}>
                        <Select
                          value={SelectedPartner == 0 ? values.partnersId : SelectedPartner}
                          id="test-select-label"
                          onChange={(e) => {
                            setFieldValue("customerName", Partners.find((item) => item.partnersId == e.target.value)?.customerName);
                            setSelectedPartner(Number(e.target.value));
                          }}
                          name="partnersId"
                          className="border-round custom-menu-item"
                          IconComponent={ExpandMoreIcon}
                          displayEmpty
                          renderValue={(selected) => {
                            const selectedPartner = Partners.find((partner) => partner.partnersId === selected);
                            return selectedPartner ? selectedPartner.customerName : "Select Client";
                          }}
                        >
                          <MenuItem key={0} value={0}>
                            Select Client
                          </MenuItem>
                          {Partners.map((partner, key) => (
                            <MenuItem key={key} value={partner.partnersId}>
                              {partner.customerName}
                            </MenuItem>
                          ))}
                        </Select>

                      <FormHelperText>{errors.partnersId}</FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl error={!!errors.cancellationDate} sx={{ width: '100%' }}>
                      <DatePicker
                        inputFormat="MM/DD/YYYY"
                        label="Cancellation Date"
                        className="select-input-border text-input-box"
                        value={(values.cancellationDate == '') ? new Date(values.cancellationDate) : values.cancellationDate}
                        onChange={(v) => {
                          if (v) {
                            var formattedDate = ''
                            try {
                              formattedDate = new Date(v).toISOString().split('.')[0];
                            } catch {
                              formattedDate = v.toLocaleString()
                            }
                            initialValues.cancellationDate = formattedDate;
                            setFieldValue("cancellationDate", formattedDate);
                          }
                        }}
                        renderInput={(params) => (
                          <TextField
                            fullWidth
                            size="small"
                            {...params}
                            id="cancellationDate"
                            name="cancellationDate"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl error={!!errors.licensee} sx={{ width: '100%' }}>
                      <FMTextField
                        label="Licensee"
                        name="licensee"
                        value={initialValues.licensee}
                        onChange={(e: any) => {
                          initialValues.licensee = e.target.value;
                          setFieldValue("licensee", e.target.value);
                        }}
                        fullWidth
                        error={!!errors.licensee}
                        helperText={errors.licensee}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl error={!!errors.licenseNumber} sx={{ width: '100%' }}>
                      <FMTextField
                        label="License Number"
                        name="licenseNumber"
                        value={initialValues.licenseNumber}
                        onChange={(e: any) => {
                          initialValues.licenseNumber = e.target.value;
                          setFieldValue("licenseNumber", e.target.value);
                        }}
                        fullWidth
                        error={!!errors.licenseNumber}
                        helperText={errors.licenseNumber}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl error={!!errors.npn} sx={{ width: '100%' }}>
                      <FMTextField
                        label="NPN"
                        name="npn"
                        value={initialValues.npn}
                        onChange={(e: any) => {
                          initialValues.npn = e.target.value;
                          setFieldValue("npn", e.target.value);
                        }}
                        fullWidth
                        error={!!errors.npn}
                        helperText={errors.npn}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl error={!!errors.fein} sx={{ width: '100%' }}>
                      <FMTextField
                        label="FEIN"
                        name="fein"
                        value={initialValues.fein}
                        onChange={(e: any) => {
                          initialValues.fein = e.target.value;
                          setFieldValue("fein", e.target.value);
                        }}
                        fullWidth
                        error={!!errors.fein}
                        helperText={errors.fein}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl sx={{ width: '100%' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={initialValues.mainLicensee}
                            onChange={(e) => {
                              initialValues.mainLicensee = e.target.checked;
                              setFieldValue("mainLicensee", e.target.checked)
                            }}
                            name="mainLicense"
                            color="primary"
                          />
                        }
                        label="Main License"
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={6}>
                    <FormControl sx={{ width: '100%' }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={initialValues.entity}
                            onChange={(e) => {
                              initialValues.entity = e.target.checked;
                              setFieldValue("entity", e.target.checked)
                            }}
                            name="entity"
                            color="primary"
                          />
                        }
                        label="Entity"
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              </DialogContent>

              <DialogActions>
                <button
                  className="btn-2 btn d-flex btn-primary search-btn"
                  type="button"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary active-btn-blue search-btn"
                  type="submit"
                  disabled={isSubmitting}
                >
                  Submit
                </button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </LocalizationProvider>
  );
}
